import React from 'react';

/**
 * Componente especializado para filtros dinámicos de la sección Evangelio
 * Analiza los títulos de los artículos para detectar tiempos litúrgicos automáticamente
 */
const EvangelioFilters = ({ articles, activeFilter, onFilterChange }) => {
  
  // Función para detectar tiempo litúrgico desde el título
  const detectLiturgicalTime = (title) => {
    const titleLower = title.toLowerCase();
    
    // Patrones de tiempos litúrgicos
    const patterns = {
      'adviento': /adviento/i,
      'navidad': /navidad|natividad/i,
      'cuaresma': /cuaresma/i,
      'pascua': /pascua|pascual/i,
      'ordinario': /tiempo\s*ordinario|ordinario/i,
      'semana-santa': /semana\s*santa|domingo\s*de\s*ramos|jueves\s*santo|viernes\s*santo|sábado\s*santo/i,
      'pentecostes': /pentecostés|pentecostes/i,
      'epifania': /epifanía|epifania/i,
      'candelaria': /candelaria|presentación/i,
      'transfiguracion': /transfiguración|transfiguracion/i,
      'todos-santos': /todos\s*los\s*santos|difuntos/i,
      'inmaculada': /inmaculada/i,
      'cristo-rey': /cristo\s*rey/i
    };
    
    // Buscar coincidencias
    for (const [time, pattern] of Object.entries(patterns)) {
      if (pattern.test(titleLower)) {
        return time;
      }
    }
    
    return 'ordinario'; // Por defecto
  };
  
  // Función para detectar número de semana o día especial
  const detectWeekOrDay = (title) => {
    // Detectar números romanos para semanas
    const romanWeek = title.match(/([IVX]+)\s*tiempo\s*ordinario/i);
    if (romanWeek) {
      return `Semana ${romanWeek[1]}`;
    }
    
    // Detectar domingos especiales
    const specialDays = title.match(/(domingo|lunes|martes|miércoles|jueves|viernes|sábado)\s*([IVX]*)\s*(tiempo\s*ordinario|adviento|cuaresma|pascua)/i);
    if (specialDays) {
      return specialDays[1].charAt(0).toUpperCase() + specialDays[1].slice(1);
    }
    
    return null;
  };
  
  // Generar filtros dinámicos basados en los artículos disponibles
  const generateDynamicFilters = () => {
    if (!articles || articles.length === 0) {
      return getDefaultFilters();
    }
    
    const liturgicalTimes = new Map();
    const weekDays = new Map();
    
    articles.forEach(article => {
      const liturgicalTime = detectLiturgicalTime(article.title);
      const weekDay = detectWeekOrDay(article.title);
      
      // Contar tiempos litúrgicos
      liturgicalTimes.set(liturgicalTime, (liturgicalTimes.get(liturgicalTime) || 0) + 1);
      
      // Contar días de la semana
      if (weekDay) {
        weekDays.set(weekDay, (weekDays.get(weekDay) || 0) + 1);
      }
    });
    
    const filters = [
      { id: 'recent', label: '✨ Todos', emoji: '✨' },
      { id: 'thisWeek', label: '📅 Esta semana', emoji: '📅' }
    ];
    
    // Agregar filtros de tiempos litúrgicos encontrados
    const liturgicalConfig = {
      'ordinario': { label: 'Tiempo Ordinario', emoji: '📖' },
      'adviento': { label: 'Adviento', emoji: '🎄' },
      'navidad': { label: 'Navidad', emoji: '⭐' },
      'cuaresma': { label: 'Cuaresma', emoji: '✨' },
      'pascua': { label: 'Pascua', emoji: '🌅' },
      'semana-santa': { label: 'Semana Santa', emoji: '✝️' },
      'pentecostes': { label: 'Pentecostés', emoji: '🕊️' },
      'epifania': { label: 'Epifanía', emoji: '👑' },
      'todos-santos': { label: 'Todos los Santos', emoji: '👼' },
      'inmaculada': { label: 'Inmaculada', emoji: '🌹' },
      'cristo-rey': { label: 'Cristo Rey', emoji: '👑' }
    };
    
    // Ordenar por relevancia (más artículos primero)
    const sortedLiturgicalTimes = Array.from(liturgicalTimes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6); // Máximo 6 filtros litúrgicos
    
    sortedLiturgicalTimes.forEach(([time, count]) => {
      const config = liturgicalConfig[time];
      if (config && count > 0) {
        filters.push({
          id: time,
          label: `${config.emoji} ${config.label} (${count})`,
          emoji: config.emoji,
          liturgicalTime: time
        });
      }
    });
    
    return filters;
  };
  
  // Filtros por defecto si no hay artículos
  const getDefaultFilters = () => [
    { id: 'recent', label: '✨ Todos', emoji: '✨' },
    { id: 'thisWeek', label: '📅 Esta semana', emoji: '📅' },
    { id: 'ordinario', label: '📖 Tiempo Ordinario', emoji: '📖' },
    { id: 'adviento', label: '🎄 Adviento', emoji: '🎄' },
    { id: 'cuaresma', label: '✨ Cuaresma', emoji: '✨' },
    { id: 'pascua', label: '🌅 Pascua', emoji: '🌅' }
  ];
  
  // Función para filtrar artículos según el filtro activo
  const filterArticles = (articles, filter) => {
    if (!articles || articles.length === 0) return [];
    
    switch (filter) {
      case 'recent':
        // Mostrar todos los artículos ordenados por fecha (más recientes primero)
        return articles.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA; // Orden descendente (más reciente primero)
        });
        
      case 'thisWeek':
        // Artículos de esta semana
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        return articles.filter(article => {
          if (article.date) {
            const articleDate = new Date(article.date);
            return articleDate >= oneWeekAgo;
          }
          return false;
        });
        
      default:
        // Filtros litúrgicos
        if (filter.startsWith('mes-')) {
          // Para compatibilidad con otros filtros
          return articles;
        }
        
        // Filtrar por tiempo litúrgico
        return articles.filter(article => {
          const liturgicalTime = detectLiturgicalTime(article.title);
          return liturgicalTime === filter;
        });
    }
  };
  
  const filters = generateDynamicFilters();
  
  return {
    filters,
    filterArticles: (articles) => filterArticles(articles, activeFilter)
  };
};

// Hook personalizado para usar los filtros de Evangelio
export const useEvangelioFilters = (articles, initialFilter = 'recent') => {
  const [activeFilter, setActiveFilter] = React.useState(initialFilter);
  
  const evangelioFilters = EvangelioFilters({ 
    articles, 
    activeFilter, 
    onFilterChange: setActiveFilter 
  });
  
  const filteredArticles = evangelioFilters.filterArticles(articles);
  
  return {
    filters: evangelioFilters.filters,
    activeFilter,
    setActiveFilter,
    filteredArticles
  };
};

// Componente visual para renderizar los filtros
export const EvangelioFilterPills = ({ filters, activeFilter, onFilterChange }) => {
  return (
    <div className="px-5 absolute inset-x-0" style={{ top: '100%', transform: 'translateY(-32px)' }}>
      <div
        className="bg-white rounded-3xl shadow-md flex gap-3 overflow-x-auto custom-scrollbar px-4 py-3"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`px-4 py-3 rounded-2xl font-semibold whitespace-nowrap text-sm transition-all duration-300 ${
              activeFilter === filter.id
                ? 'text-white transform -translate-y-1'
                : 'bg-white text-gray-600 hover:border-red-500 transform hover:-translate-y-0.5'
            }`}
            style={
              activeFilter === filter.id
                ? {
                    background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
                    boxShadow: '0 6px 20px rgba(230, 44, 25, 0.3)'
                  }
                : {
                    background: '#f8f9fa',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    border: '2px solid transparent'
                  }
            }
          >
            {filter.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EvangelioFilters;
